import { window } from "global";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { MediaQuery } from "@outplayed/responsive";
import { ReactComponent as SearchIcon } from "@ugg/shared/assets/svg/search-icon.svg";
import { TierListHeader } from "@ugg/shared/components/TierList/TierListHeader";
import { TierListPageNav } from "@ugg/shared/components/TierList/TierListPageNav";
import { TierListFilterManager } from "@ugg/shared/components/TierList/TierListFilterManager";
import { TierListTablePlaceholder } from "@ugg/shared/components/TierList/common/TierListTablePlaceholder";
import { TierListTableError } from "@ugg/shared/components/TierList/common/TierListTableError";
import { useQueryString } from "@ugg/shared/hooks/use-query-string";
import { SPECIAL_TIER_LIST_PAGES } from "@ugg/shared/pages/tier-list-pages";
import { useValidateQueryParams } from "@ugg/shared/query-params/params-helpers";
import { validParams } from "@ugg/shared/query-params/valid-params";
import { buildQueryParams } from "@ugg/shared/utils/url-query-params";
import {
  ClassicChampionsManifest,
  ClassicTierEntry,
  getClassicChampionImgUrl,
  getClassicRoleLetter,
  getClassicRoleName,
  useClassicChampions,
  useClassicChampionTierList,
} from "@ugg/shared/hooks/classic";
import { getRoleSVG, RoleS } from "@ugg/shared/utils/role-helpers";
import { getClassicChampionBuildUrl } from "@ugg/shared/routes/app-routes";

// Reuse the ARAM Mayhem per-tier color maps — the Classic tier list uses
// the same visual treatment (colored banner + divider + zebra-striped
// body). Kept as a shared import rather than re-declared so the two
// special-mode tier lists stay visually in lockstep. (These live in
// tier-helpers.ts alongside a TODO to pay down the AI-generated styling
// debt post-launch; when that happens, both pages move together.)
import {
  ALL_TIERS,
  TierNameValue,
  ARAM_MAYHEM_TIER_LABEL_BG,
  ARAM_MAYHEM_TIER_BANNER_CLASSES,
  ARAM_MAYHEM_TIER_BODY_BG,
} from "@ugg/shared/utils/tier-helpers";

// Defensive coercions (mirrors the Classic champion profile): the tier-list JSON
// is part of the same hand-authored, still-evolving Classic pipeline, so a field
// can arrive with an unexpected shape without TypeScript noticing. These keep a
// bad shape rendering empty instead of throwing on `.map`/`.filter`/destructure.
const asText = (value: unknown): string => (typeof value === "string" ? value : "");
function asArray<T>(value: readonly T[] | null | undefined): T[] {
  return Array.isArray(value) ? (value as T[]) : [];
}

type BannerProp = {
  bannerSlot?: JSX.Element | null;
  patchNotesCTA?: React.ReactNode;
};

// Patch that the current Classic tier list is sourced from PBE data for.
// The PBE info CTA below only renders on this patch; on any other patch
// it's hidden (the data is expected to be live/final by then).
const CLASSIC_PBE_PATCH = "16_14";

// Maps the Classic tier-list role letter (T/J/M/B/S, as carried in the
// tier-list JSON) to the site RoleS value, so the table can render the
// same white role icons used by the filter chips (getRoleSVG) rather than
// the custom Classic role webp set.
const CLASSIC_ROLE_TO_SITE_ROLE: Record<string, RoleS> = {
  T: RoleS.TOP,
  J: RoleS.JUNGLE,
  M: RoleS.MIDDLE,
  B: RoleS.BOTTOM,
  S: RoleS.SUPPORT,
};

// ─────────────────────────────────────────────────────────────────────
// PBE info CTA
// ─────────────────────────────────────────────────────────────────────
//
// Non-link variant of the home-page "sticky-section" CTA (black card,
// colored border, bold colored label + body copy). Purely informational
// — no anchor, no Read More button. Sits at the top of the tier-list
// panel, below the filters and above the S+ tier group.

// Inline 4-point sparkle so the graphic doesn't depend on the RemixIcon
// font being loaded in every render context (the site loads it globally,
// the desktop app may not).
function SparkleIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className={className}>
      <path d="M12 1c1.1 4.9 2 5.8 6.9 6.9C14 9 13.1 9.9 12 14.8 10.9 9.9 10 9 5.1 7.9 10 6.8 10.9 5.9 12 1Z" />
      <path d="M18.5 14c.5 2.3.9 2.7 3.2 3.2-2.3.5-2.7.9-3.2 3.2-.5-2.3-.9-2.7-3.2-3.2 2.3-.5 2.7-.9 3.2-3.2Z" />
    </svg>
  );
}

function ClassicPbeInfoCTA() {
  return (
    <div className="mb-[24px] flex flex-col items-start gap-x-[6px] gap-y-[4px] rounded-[6px] border border-[#3273FA] bg-black px-[19px] py-[9px] md:flex-row md:items-center">
      <div className="flex shrink-0 items-center gap-[12px]">
        <SparkleIcon className="h-5 w-5 shrink-0 text-[#3273FA]" />
        <span className="whitespace-nowrap text-[14px] font-extrabold leading-[24px] text-[#3273FA]">PBE DISCLAIMER</span>
      </div>
      <span className="text-[14px] leading-[24px] text-white">
        This Classic Tier List is based on an unreleased version of the game. Stay tuned for additional ranking changes.
      </span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Champion search
// ─────────────────────────────────────────────────────────────────────
//
// Mirrors the Items Page "Search Item" control (dark pill input + right-
// aligned search glyph). The "invisible text" is just a native
// placeholder: it reads "Search Champion" while empty and disappears once
// the user types. EXPERIMENTAL — kept local to the Classic container so
// it's easy to pull out if it doesn't pan out.
function ClassicChampionSearch({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  return (
    <div className="relative flex items-center">
      <input
        type="text"
        placeholder="Search Champion"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label="Search Champion"
        className="h-9 w-[220px] rounded-[6px] border border-[#25254b] bg-[#25254b] pl-[14px] pr-[34px] text-[14px] font-normal leading-5 text-[#cddcfe] placeholder:text-[#cddcfe] focus:border-[#3273fa] focus:outline-none"
      />
      <SearchIcon className="pointer-events-none absolute right-[12px] w-4 [&_path]:fill-[#cddcfe]" />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Table
// ─────────────────────────────────────────────────────────────────────

interface ClassicChampionTierListTableProps {
  // Partial: absent tiers are omitted from the payload (see ClassicChampionTierList).
  // The table already guards each tier before mapping, so undefined is expected.
  tierlist: Partial<Record<TierNameValue, ClassicTierEntry[]>>;
  manifest: ClassicChampionsManifest;
  // Query string (e.g. "patch=16_14") carried onto each champion's build link so
  // clicking through keeps the currently-selected patch — mirrors the shared
  // ChampionLink, which passes the tier list's URL params to the build page.
  // Empty on the latest patch (no `?patch=`), so canonical links stay clean.
  championLinkSearch?: string;
}

export function ClassicChampionTierListTable(props: ClassicChampionTierListTableProps) {
  const { tierlist, manifest, championLinkSearch } = props || {};

  return (
    <div className="flex flex-col gap-6">
      {ALL_TIERS.map((tier) => {
        // Normalize to well-formed [key, role] entries up front so a malformed
        // tier payload (non-array tier, or entries that aren't [key, role] pairs)
        // degrades to empty rows instead of throwing on the destructures below.
        const champions = asArray(tierlist[tier])
          .filter((entry) => Array.isArray(entry))
          .map((entry) => [entry[0], asText(entry[1])] as ClassicTierEntry);
        if (champions.length === 0) return null;
        // Sort each tier alphabetically by champion display name to mirror
        // the tier-list building tool's non-manual ordering. Falls back to
        // the raw key for champions missing from the metadata manifest, and
        // breaks name ties by role letter so the order stays stable.
        const sortedChampions = [...champions].sort(([keyA, roleA], [keyB, roleB]) => {
          const nameA = asText(manifest[String(keyA)]?.[1]) || `#${keyA}`;
          const nameB = asText(manifest[String(keyB)]?.[1]) || `#${keyB}`;
          return nameA.localeCompare(nameB) || roleA.localeCompare(roleB);
        });
        return (
          <section key={tier} aria-label={`Tier ${tier}`} className="overflow-hidden rounded-[3px]">
            <div
              className={`flex h-14 items-center gap-3 border-l-4 bg-[#191937] bg-gradient-to-r to-transparent px-[18px] ${ARAM_MAYHEM_TIER_BANNER_CLASSES[tier]}`}
            >
              <span className={`block h-5 w-0.5 rounded-sm ${ARAM_MAYHEM_TIER_LABEL_BG[tier]}`} aria-hidden="true" />
              <h2 className="text-sm font-bold text-white">{tier}</h2>
            </div>
            <div className={`h-px ${ARAM_MAYHEM_TIER_LABEL_BG[tier]}`} aria-hidden="true" />
            <div
              className={`grid grid-cols-1 gap-x-[18px] gap-y-[18px] border-l-4 p-[18px] sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 ${ARAM_MAYHEM_TIER_BODY_BG[tier]} ${ARAM_MAYHEM_TIER_BANNER_CLASSES[tier]}`}
            >
              {sortedChampions.map(([championKey, role]) => {
                const displayName = manifest[String(championKey)]?.[1];
                const alias = manifest[String(championKey)]?.[0];

                // No display name yet (manifest still loading / champion
                // missing from the metadata file) — fall back to the raw
                // key so the row still renders and the layout holds.
                if (!displayName) {
                  return (
                    <div key={`${championKey}-${role}`} className="flex items-center gap-2">
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[3px] border border-dashed border-[#1c2030] bg-[#1a1d2e] font-mono text-[10px] text-[#64748b]">
                        ?
                      </div>
                      <span className="font-mono text-sm text-[#cddcea]">
                        #{championKey} [{role}]
                      </span>
                    </div>
                  );
                }

                // Champion images come from the Classic custom CDN (not
                // riot-assets). White role icon shared with the filter chips
                // (getRoleSVG default variant), keyed off the tier-list role
                // letter. Rendered between the portrait and the name.
                const roleIcon = getRoleSVG(CLASSIC_ROLE_TO_SITE_ROLE[role?.toUpperCase()]);

                const tileContent = (
                  <>
                    <img
                      src={getClassicChampionImgUrl(championKey)}
                      alt={displayName}
                      title={displayName}
                      loading="lazy"
                      className="h-9 w-9 shrink-0 rounded-[3px]"
                    />
                    {roleIcon && (
                      <span className="flex h-4 w-4 shrink-0 items-center justify-center" title={getClassicRoleName(role)}>
                        {roleIcon}
                      </span>
                    )}
                    <span className="font-[Barlow] text-sm font-semibold text-white group-hover/champion-link:underline">
                      {displayName}
                    </span>
                  </>
                );

                // Link each champion to its Classic build page. Falls back to a
                // plain (non-link) tile if the alias is missing from the manifest.
                return alias ? (
                  <Link
                    key={`${championKey}-${role}`}
                    to={{ pathname: getClassicChampionBuildUrl(alias), search: championLinkSearch }}
                    className="group/champion-link flex items-center gap-2"
                  >
                    {tileContent}
                  </Link>
                ) : (
                  <div key={`${championKey}-${role}`} className="flex items-center gap-2">
                    {tileContent}
                  </div>
                );
              })}
            </div>
          </section>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Container
// ─────────────────────────────────────────────────────────────────────

export const ClassicChampionTierListContainer = ({ bannerSlot, patchNotesCTA }: BannerProp) => {
  const queryParams = useQueryString();

  const validateQueryParams = useValidateQueryParams(validParams);
  const validatedQueryParams = validateQueryParams(SPECIAL_TIER_LIST_PAGES.CLASSIC_TIER_LIST, queryParams, true);
  const { patch } = validatedQueryParams;

  // Splice the validated patch back into the params dict so the tier-list
  // hook resolves the same patch even when the URL omits `?patch=...`
  // (mirrors the ARAM Mayhem containers).
  const params = { ...queryParams, patch };

  // Query string carried onto each champion's Classic build link so clicking a
  // champion keeps the selected patch (e.g. `?patch=16_14` → the build page's
  // version filter). Only carried when the URL explicitly has a `?patch=` — on
  // the latest patch there's no param, so links stay canonical (no `?patch=`).
  // Uses the VALIDATED patch (not the raw value) so a bad param can't leak
  // through. Patch only (not role/region/queue): the Classic build page takes
  // role as a path segment, so passing the other filters as query would just
  // clutter the URL. Matches the shared ChampionLink's param pass-through.
  const championLinkSearch = queryParams.patch ? buildQueryParams({ patch }) : "";

  // Champion metadata (key → [alias, displayName]) for the Classic
  // roster. Fetched with `ssr: true` so the names are prefetched during
  // server render and hydrate cleanly on the client (SSR + JSONFetcher
  // handling mirrors the other special-mode data hooks). URL is patch-
  // independent, so there's no patch gating here.
  const { data: manifestData, loading: manifestLoading, error: manifestError } = useClassicChampions({ ssr: true });
  const manifest = manifestData || ({} as ClassicChampionsManifest);

  // Tier list data — per-patch file from the Classic CDN pipeline. The
  // patch filter swaps the version segment of the URL.
  const {
    data: tierlistData,
    loading: tierlistLoading,
    error: tierlistError,
  } = useClassicChampionTierList({ params, ssr: true });
  const { tierlist, lastUpdated } = tierlistData || {};

  // Role filter. "All Roles" (the default) shows every champion/role
  // entry; picking a lane narrows each tier to the entries tagged with
  // that role letter. getClassicRoleLetter resolves the query value —
  // including the SEO "adc" alias for bot — to the tier-list letter, and
  // returns undefined for "all", meaning "don't filter".
  const roleLetter = getClassicRoleLetter(validatedQueryParams.role);
  const filteredTierlist = useMemo(() => {
    const base = tierlist || ({} as Record<TierNameValue, ClassicTierEntry[]>);
    if (!roleLetter) return base;
    const result = {} as Record<TierNameValue, ClassicTierEntry[]>;
    for (const tier of ALL_TIERS) {
      result[tier] = asArray(base[tier]).filter((entry) => asText(entry?.[1]).toUpperCase() === roleLetter);
    }
    return result;
  }, [tierlist, roleLetter]);

  // Champion name search — layered on top of the role filter, so it
  // narrows within the current role selection (this scoping may change
  // based on feedback). Case-insensitive substring match against the
  // display name; an empty query is a no-op. EXPERIMENTAL feature.
  const [search, setSearch] = useState("");
  const searchedTierlist = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return filteredTierlist;
    const result = {} as Record<TierNameValue, ClassicTierEntry[]>;
    for (const tier of ALL_TIERS) {
      result[tier] = asArray(filteredTierlist[tier]).filter((entry) => {
        const name = manifest[String(entry?.[0])]?.[1];
        return typeof name === "string" ? name.toLowerCase().includes(query) : false;
      });
    }
    return result;
  }, [filteredTierlist, manifest, search]);

  // Gate the table on BOTH fetches: the tier list supplies the rows and
  // the manifest supplies the champion names, so defer until both are
  // ready rather than flashing placeholder names then the real ones.
  const loading = manifestLoading || tierlistLoading;
  const error = manifestError || tierlistError;

  useEffect(() => {
    window && window.scrollTo(0, 0);
  }, []);

  // Both the manifest and the tier list are prefetched with `ssr: true`, so
  // they're present during server render — we render the table server-side for
  // SEO (mirroring the Classic home grid) rather than gating on `window`. Server
  // and client first render match (same prefetched data hydrated from the SSR
  // cache), so there's no hydration mismatch; `loading="lazy"` imgs are valid in
  // SSR markup. Falls back to placeholder/error the same way while data resolves.
  let table;
  if (loading) {
    table = <TierListTablePlaceholder />;
  } else if (error) {
    table = <TierListTableError />;
  } else {
    table = (
      <ClassicChampionTierListTable
        tierlist={searchedTierlist}
        manifest={manifest}
        championLinkSearch={championLinkSearch}
      />
    );
  }

  return (
    // Reserve a minimum content height on desktop (md+, where the shared
    // TierListPage renders the sticky ad rails) so short/filtered/no-result
    // states don't collapse the content column — which would otherwise let
    // the rails' sticky containing block shrink and make the ad units drift
    // upward. Scoped to md+ so mobile (no rails) doesn't get dead space.
    // Stopgap kept local to Classic; a shared fix is a separate follow-up.
    <div className="md:min-h-[800px]">
      <TierListHeader
        className="mb-[24px]"
        totalMatches={0}
        lastUpdated={lastUpdated}
        loading={loading}
        includeMeta
        patchNotesCTA={patchNotesCTA}
      />
      <MediaQuery min="TABLET" max="DESKTOP_LARGE">
        <div>
          <TierListPageNav />
        </div>
      </MediaQuery>
      {bannerSlot ?? null}
      <TierListFilterManager className="my-[24px]" endSlot={<ClassicChampionSearch value={search} onChange={setSearch} />} />
      {patch === CLASSIC_PBE_PATCH && <ClassicPbeInfoCTA />}
      <>{table}</>
    </div>
  );
};
